// src/pages/About.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import aboutVideo from '../assets/about.mp4';

function About() {
  const { t } = useTranslation();
  const [counters, setCounters] = useState({ years: 0, clients: 0, projects: 0 });
  const statsRef = useRef(null);

  useEffect(() => {
    const targets = { years: 10, clients: 5000, projects: 150 };
    const duration = 2000;
    const step = 20;
    const increments = {
      years: targets.years / (duration / step),
      clients: targets.clients / (duration / step),
      projects: targets.projects / (duration / step),
    };

    const animate = () => {
      let current = { years: 0, clients: 0, projects: 0 };
      const timer = setInterval(() => {
        let done = true;
        if (current.years < targets.years)       { current.years    = Math.min(current.years    + increments.years,    targets.years);    done = false; }
        if (current.clients < targets.clients)   { current.clients  = Math.min(current.clients  + increments.clients,  targets.clients);  done = false; }
        if (current.projects < targets.projects) { current.projects = Math.min(current.projects + increments.projects, targets.projects); done = false; }
        setCounters({
          years: Math.floor(current.years),
          clients: Math.floor(current.clients),
          projects: Math.floor(current.projects),
        });
        if (done) clearInterval(timer);
      }, step);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animate();
          observer.disconnect();
        }
      });
    });

    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <main className="about-page">
      <section className="page-hero">
        <div className="page-hero__inner">
          <span className="page-hero__label">{t('about.heroLabel')}</span>
          <h1 className="page-hero__title">{t('about.heroTitle')}</h1>
          <p className="page-hero__subtitle">{t('about.heroSubtitle')}</p>
        </div>
      </section>

      <section className="about-intro">
        <div className="about-intro__inner">
          <div className="about-video">
            <video autoPlay loop muted playsInline poster="/img/about-poster.jpg" className="about-video__player">
              <source src={aboutVideo} type="video/mp4" />
            </video>
            <div className="about-video__badge">
              <span className="about-video__dot" />
              {t('about.videoPlaying')}
            </div>
          </div>

          <div className="about-intro__text">
            <span className="about-intro__badge">{t('about.badge')}</span>
            <h2>{t('about.heading')}</h2>
            <p>{t('about.p1')}</p>
            <p>{t('about.p2')}</p>

            <div className="about-stats" ref={statsRef}>
              <div className="about-stat">
                <div className="about-stat__value">{counters.years}+</div>
                <div className="about-stat__label">{t('about.statYears')}</div>
              </div>
              <div className="about-stat">
                <div className="about-stat__value">{counters.clients.toLocaleString()}+</div>
                <div className="about-stat__label">{t('about.statClients')}</div>
              </div>
              <div className="about-stat">
                <div className="about-stat__value">{counters.projects}+</div>
                <div className="about-stat__label">{t('about.statProjects')}</div>
              </div>
            </div>

            <Link to="/contact" className="about-intro__cta">
              {t('about.ctaMore')} <span>→</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="about-values">
        <header className="about-values__head">
          <span className="about-values__label">{t('about.valuesLabel')}</span>
          <h2 className="about-values__title">{t('about.valuesTitle')}</h2>
        </header>

        <div className="about-values__grid">
          <div className="about-value">
            <div className="about-value__icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2 3 7v6c0 5 4 9 9 9s9-4 9-9V7l-9-5z" />
              </svg>
            </div>
            <h3>{t('about.value1Title')}</h3>
            <p>{t('about.value1Text')}</p>
          </div>

          <div className="about-value">
            <div className="about-value__icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <h3>{t('about.value2Title')}</h3>
            <p>{t('about.value2Text')}</p>
          </div>

          <div className="about-value">
            <div className="about-value__icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <h3>{t('about.value3Title')}</h3>
            <p>{t('about.value3Text')}</p>
          </div>
        </div>
      </section>

      <section className="about-quote">
        <div className="about-quote__inner">
          <span className="about-quote__mark">"</span>
          <p>{t('about.quoteText')}</p>
          <span className="about-quote__author">{t('about.quoteAuthor')}</span>
        </div>
      </section>

      <section className="about-cta">
        <h2>{t('about.ctaTitle')}</h2>
        <p>{t('about.ctaText')}</p>
        <Link to="/boutique" className="btn btn-primary">
          {t('about.ctaButton')} <span>→</span>
        </Link>
      </section>
    </main>
  );
}

export default About;